import React, { useState, useEffect, useCallback } from 'react';
import { saveTemplates, loadTemplates, type TemplateInfo } from '../../utils/templateGalleryDB';
import { 
  TEMPLATES_PATH,
  getTemplateZipUrl, 
  getThumbnailUrl,
  getTemplatesListUrl
} from '../../config/templateConfig';
import './TemplateGallery.css';

interface TemplateGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (templateName: string, templateUrl?: string) => void;
}

export const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  isOpen,
  onClose,
  onSelectTemplate
}) => {
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<Map<string, string[]>>(new Map()); // templateId -> 썸네일 URL 배열
  const [isScanned, setIsScanned] = useState(false); // 템플릿 스캔 완료 여부
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateInfo | null>(null); // 확대보기 모달에서 선택된 템플릿
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState<number>(0); // 확대보기 모달에서 선택된 썸네일 인덱스
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false); // 확대보기 모달 열림 여부

  // IndexedDB에서 템플릿 목록 불러오기
  const loadCachedTemplates = useCallback(async () => {
    try {
      const cached = await loadTemplates();
      if (cached.length > 0) {
        setTemplates(cached);
        // 썸네일 URL 로드
        await loadThumbnails(cached);
        setIsScanned(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error('캐시된 템플릿 불러오기 실패:', err);
      return false;
    }
  }, []);

  // 썸네일 URL 로드 (thumbnailPaths에서 URL 생성)
  const loadThumbnails = useCallback(async (templateList: TemplateInfo[]) => {
    const newThumbnails = new Map<string, string[]>();
    
    for (const template of templateList) {
      // thumbnailPaths에서 URL 생성
      if (template.thumbnailPaths && template.thumbnailPaths.length > 0) {
        const thumbnailUrls = template.thumbnailPaths.map(fileName => 
          getThumbnailUrl(template.name, fileName)
        );
        newThumbnails.set(template.id, thumbnailUrls);
      } else {
        // 썸네일이 없는 경우 빈 배열
        newThumbnails.set(template.id, []);
      }
    }

    setThumbnails(newThumbnails);
  }, []);

  // 웹 서버에서 템플릿 목록 가져오기
  const scanTemplatesFolder = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 먼저 서버에서 템플릿 목록 JSON을 가져오려고 시도
      let templateList: string[] = [];
      
      try {
        const listUrl = getTemplatesListUrl();
        const response = await fetch(listUrl);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data.templates)) {
            templateList = data.templates;
          } else if (Array.isArray(data)) {
            templateList = data;
          }
        }
      } catch (err) {
        console.log('템플릿 목록 API를 사용할 수 없습니다. 디렉터리 리스팅을 시도합니다.');
      }

      // 템플릿 목록이 없으면 고정된 템플릿 목록 사용 (template_0001 ~ template_0026)
      if (templateList.length === 0) {
        for (let i = 1; i <= 26; i++) {
          const templateName = `template_${String(i).padStart(4, '0')}`;
          templateList.push(templateName);
        }
      }

      // 각 템플릿에 대해 ZIP 파일 존재 여부 확인 및 썸네일 찾기
      const templateFolders: TemplateInfo[] = [];
      const folderThumbnails = new Map<string, string[]>();

      for (const templateName of templateList) {
        try {
          // ZIP 파일 존재 확인
          const zipUrl = getTemplateZipUrl(templateName);
          const zipResponse = await fetch(zipUrl, { method: 'HEAD' });
          
          if (!zipResponse.ok) {
            continue; // ZIP 파일이 없으면 스킵
          }

          // 썸네일 이미지 찾기 (일반적인 썸네일 파일명 패턴)
          const thumbnailPatterns = [
            'thumb_1200x1200.jpg',
            'thumb_1200x1500.jpg',
            'thumb_1200x1920.jpg',
            'thumb_1200x628.jpg'
          ];

          const thumbnailPaths: string[] = [];
          const thumbnailUrls: string[] = [];

          for (const pattern of thumbnailPatterns) {
            const thumbnailUrl = getThumbnailUrl(templateName, pattern);
            try {
              const thumbResponse = await fetch(thumbnailUrl, { method: 'HEAD' });
              if (thumbResponse.ok) {
                thumbnailPaths.push(pattern);
                thumbnailUrls.push(thumbnailUrl);
              }
            } catch (err) {
              // 썸네일이 없으면 스킵
            }
          }

          // 템플릿 정보 생성
          templateFolders.push({
            id: templateName,
            name: templateName,
            thumbnailPaths: thumbnailPaths,
            folderPath: `${TEMPLATES_PATH}/${templateName}/${templateName}.zip`,
            lastModified: Date.now()
          });
          
          folderThumbnails.set(templateName, thumbnailUrls);
        } catch (err) {
          console.error(`템플릿 "${templateName}" 확인 실패:`, err);
        }
      }

      if (templateFolders.length === 0) {
        alert('템플릿을 찾을 수 없습니다.\n\n서버의 템플릿 경로를 확인해주세요.');
        setLoading(false);
        return;
      }

      // IndexedDB에 저장
      await saveTemplates(templateFolders);
      setTemplates(templateFolders);
      setThumbnails(folderThumbnails);
      setIsScanned(true);
    } catch (err: any) {
      console.error('템플릿 스캔 실패:', err);
      setError('템플릿을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 칸 클릭 핸들러 (확대보기 모달 열기)
  const handleItemClick = useCallback((template: TemplateInfo) => {
    setSelectedTemplate(template);
    setSelectedThumbnailIndex(0);
    setIsDetailModalOpen(true);
  }, []);

  // 템플릿 불러오기 핸들러
  const handleLoadTemplate = useCallback(async (template: TemplateInfo) => {
    // 템플릿이 스캔되지 않았으면 먼저 스캔 요청
    if (!isScanned || templates.length === 0) {
      const shouldScan = window.confirm('템플릿 목록을 먼저 불러와야 합니다.\n\n템플릿 목록을 불러오시겠습니까?');
      if (shouldScan) {
        await scanTemplatesFolder();
        // 스캔 후 다시 시도하려면 사용자가 다시 클릭해야 함
        return;
      } else {
        return;
      }
    }

    try {
      // 템플릿 ZIP 파일 URL 생성
      const templateUrl = getTemplateZipUrl(template.name);
      
      // 템플릿 이름과 URL 전달
      onSelectTemplate(template.name, templateUrl);
      onClose();
    } catch (err: any) {
      console.error('템플릿 불러오기 실패:', err);
      alert('템플릿을 불러오는 중 오류가 발생했습니다.');
    }
  }, [isScanned, templates.length, onSelectTemplate, onClose, scanTemplatesFolder]);

  // 랜덤 템플릿 선택 핸들러
  const handleRandomTemplate = useCallback(() => {
    if (templates.length === 0) {
      return;
    }
    
    // 랜덤 인덱스 선택
    const randomIndex = Math.floor(Math.random() * templates.length);
    const randomTemplate = templates[randomIndex];
    
    // 기존 handleLoadTemplate 함수 재사용
    handleLoadTemplate(randomTemplate);
  }, [templates, handleLoadTemplate]);

  // 썸네일 호버 핸들러
  const handleThumbnailHover = useCallback((index: number) => {
    setSelectedThumbnailIndex(index);
  }, []);

  // 모달이 열릴 때 캐시된 템플릿 불러오기
  useEffect(() => {
    if (isOpen) {
      loadCachedTemplates();
    }
  }, [isOpen, loadCachedTemplates]);

  if (!isOpen) return null;

  return (
    <div className="template-gallery-overlay" onClick={onClose}>
      <div className="template-gallery-modal" onClick={(e) => e.stopPropagation()}>
        <div className="template-gallery-header">
          <h2>템플릿 갤러리</h2>
          <button className="template-gallery-close" onClick={onClose}>×</button>
        </div>

        <div className="template-gallery-toolbar">
          <div className="template-gallery-toolbar-left">
            <button 
              className="template-gallery-scan-button"
              onClick={scanTemplatesFolder}
              disabled={loading}
            >
              {loading ? '스캔 중...' : isScanned ? '🔄 템플릿 목록 다시 불러오기' : '📁 템플릿 목록 불러오기'}
            </button>
            {isScanned && (
              <span className="template-gallery-count" style={{ marginLeft: '8px', color: '#28a745' }}>
                ✓ 템플릿 목록 불러옴
              </span>
            )}
            {templates.length > 0 && (
              <span className="template-gallery-count">
                {templates.length}개의 템플릿
              </span>
            )}
          </div>
          <button
            className="template-gallery-random-button"
            onClick={handleRandomTemplate}
            disabled={loading || templates.length === 0}
            title="랜덤 템플릿 불러오기"
          >
            🎲 랜덤 템플릿
          </button>
        </div>

        {error && (
          <div className="template-gallery-error">
            {error}
          </div>
        )}

        <div className="template-gallery-content">
          {templates.length === 0 ? (
            <div className="template-gallery-empty">
              <p>템플릿이 없습니다.</p>
              <p>템플릿 목록을 불러와서 템플릿을 추가하세요.</p>
            </div>
          ) : (
            <div className="template-gallery-grid">
              {templates.map((template) => {
                const thumbnailList = thumbnails.get(template.id) || [];
                return (
                  <div
                    key={template.id}
                    className="template-gallery-item"
                  >
                    <div 
                      className="template-gallery-thumbnails"
                      onClick={() => handleItemClick(template)}
                    >
                      {thumbnailList.length > 0 ? (
                        thumbnailList.map((base64, index) => (
                          <img
                            key={index}
                            src={base64}
                            alt={`${template.name} - ${index + 1}`}
                            className="template-gallery-thumbnail"
                          />
                        ))
                      ) : (
                        <div className="template-gallery-thumbnail-placeholder">
                          이미지 없음
                        </div>
                      )}
                    </div>
                    <div className="template-gallery-item-footer">
                      <div className="template-gallery-item-name">
                        {template.name}
                      </div>
                      <button
                        className="template-gallery-load-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadTemplate(template);
                        }}
                      >
                        템플릿 불러오기
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 확대보기 모달 */}
      {isDetailModalOpen && selectedTemplate && (
        <div className="template-gallery-detail-overlay" onClick={(e) => {
          e.stopPropagation(); // 이벤트 버블링 방지 - 갤러리 모달이 닫히지 않도록
          setIsDetailModalOpen(false);
        }}>
          <div className="template-gallery-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="template-gallery-detail-header">
              <h3>{selectedTemplate.name}</h3>
              <button 
                className="template-gallery-close" 
                onClick={() => setIsDetailModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="template-gallery-detail-content">
              <div className="template-gallery-detail-main-image">
                {(() => {
                  const thumbnailList = thumbnails.get(selectedTemplate.id) || [];
                  if (thumbnailList.length > 0 && thumbnailList[selectedThumbnailIndex]) {
                    return (
                      <img
                        src={thumbnailList[selectedThumbnailIndex]}
                        alt={`${selectedTemplate.name} - ${selectedThumbnailIndex + 1}`}
                        className="template-gallery-detail-image"
                      />
                    );
                  }
                  return (
                    <div className="template-gallery-detail-placeholder">
                      이미지 없음
                    </div>
                  );
                })()}
              </div>
              <div className="template-gallery-detail-thumbnails">
                {(() => {
                  const thumbnailList = thumbnails.get(selectedTemplate.id) || [];
                  if (thumbnailList.length > 0) {
                    return thumbnailList.map((base64, index) => (
                      <div
                        key={index}
                        className={`template-gallery-detail-thumbnail-item ${
                          index === selectedThumbnailIndex ? 'active' : ''
                        }`}
                        onMouseEnter={() => handleThumbnailHover(index)}
                        onClick={() => handleThumbnailHover(index)}
                      >
                        <img
                          src={base64}
                          alt={`${selectedTemplate.name} - ${index + 1}`}
                          className="template-gallery-detail-thumbnail"
                        />
                      </div>
                    ));
                  }
                  return null;
                })()}
              </div>
            </div>
            <div className="template-gallery-detail-footer">
              <button
                className="template-gallery-detail-load-button"
                onClick={() => {
                  handleLoadTemplate(selectedTemplate);
                  setIsDetailModalOpen(false);
                }}
              >
                템플릿 불러오기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

